import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { errorMessage } from '../lib/supabase'
import type {
  EventDraft,
  EventFormErrors,
  EventFormValues,
  RetreatEvent,
} from '../types/events'
import { toDraft, toFormValues, validateEventForm } from '../utils/eventForm'

export type SaveAction = 'publish' | 'draft'

interface UseEventFormOptions {
  event: RetreatEvent | null
  onSave: (draft: EventDraft) => Promise<unknown>
  onDelete: () => Promise<void>
  onDirtyChange: (dirty: boolean) => void
}

export function useEventForm({ event, onSave, onDelete, onDirtyChange }: UseEventFormOptions) {
  const initial = useMemo(() => toFormValues(event), [event])
  const [values, setValues] = useState<EventFormValues>(initial)
  const [errors, setErrors] = useState<EventFormErrors>({})
  const [pending, setPending] = useState<SaveAction | null>(null)
  const [saveError, setSaveError] = useState<{ message: string; action: SaveAction } | null>(
    null,
  )
  const [posterBusy, setPosterBusy] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initial),
    [values, initial],
  )

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const setField = useCallback(
    <K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) => {
      setValues((previous) => ({ ...previous, [key]: value }))
      if (key === 'title' || key === 'eventDate') {
        setErrors((previous) => {
          if (!previous[key]) return previous
          const next = { ...previous }
          delete next[key]
          return next
        })
      }
    },
    [],
  )

  const runSave = useCallback(
    async (action: SaveAction) => {
      const found = validateEventForm(values)
      if (Object.keys(found).length > 0) {
        setErrors(found)
        return
      }
      setPending(action)
      setSaveError(null)
      try {
        await onSave(toDraft(values, action === 'publish'))
      } catch (caught) {
        if (mounted.current) setSaveError({ message: errorMessage(caught), action })
      } finally {
        if (mounted.current) setPending(null)
      }
    },
    [values, onSave],
  )

  const confirmDelete = useCallback(async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await onDelete()
    } catch (caught) {
      if (!mounted.current) return
      setDeleteError(errorMessage(caught))
      setDeleting(false)
    }
  }, [onDelete])

  const busy = pending !== null || posterBusy || deleting

  return {
    values,
    errors,
    isDirty,
    busy,
    pending,
    saveError,
    setField,
    runSave,
    setPosterBusy,
    confirmingDelete,
    setConfirmingDelete,
    deleting,
    deleteError,
    confirmDelete,
    previewDraft: toDraft(values, true),
  }
}
